import { RefObject, useEffect } from 'react';
import css from './InlineConfirm.module.scss';

interface InlineConfirmProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Click-outside boundary. The overlay is `position: absolute; inset: 0`
   * inside its parent, so the parent should normally also be the boundary.
   * Click outside this element while `open` is true → onCancel().
   */
  outsideRef: RefObject<HTMLElement | null>;
  confirmLabel: string;
  /** Optional second line under the confirm button (lighter weight). */
  confirmSubLabel?: string;
  cancelLabel: string;
}

/**
 * Two-button overlay confirmation that paints the entire parent surface.
 * Red half = confirm, green half = cancel; the shared seam in the middle
 * has 0 radius, outer edges round to match the parent's `border-radius`.
 *
 * Caller owns the `open` state — useful when the overlay should also auto-
 * dismiss based on changing conditions (e.g. the batch left the uploading
 * state). For pure click-to-confirm + click-outside-to-dismiss the caller
 * just toggles `open` from a local useState.
 */
export function InlineConfirm({
  open,
  onConfirm,
  onCancel,
  outsideRef,
  confirmLabel,
  confirmSubLabel,
  cancelLabel,
}: InlineConfirmProps) {
  // Click-outside dismiss on mousedown so the user sees the overlay
  // disappear immediately, before downstream click handlers fire.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const el = outsideRef.current;
      if (el && !el.contains(e.target as Node)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onCancel, outsideRef]);

  if (!open) return null;

  return (
    <div className={css.overlay}>
      <button type="button" className={`${css.half} ${css.confirm}`} onClick={onConfirm}>
        <span className={css.main}>{confirmLabel}</span>
        {confirmSubLabel && <span className={css.sub}>{confirmSubLabel}</span>}
      </button>
      <button type="button" className={`${css.half} ${css.cancel}`} onClick={onCancel}>
        <span className={css.main}>{cancelLabel}</span>
      </button>
    </div>
  );
}
